import type { RoleId } from './ids'
import { ROLES } from './roles'

export const MIN_PLAYERS = 3
export const MAX_PLAYERS = 12
export const BASE_CENTER_CARDS = 3

export type RoleCounts = Partial<Record<RoleId, number>>

/** Number of center cards for a role selection (Alpha Wolf adds the sideways werewolf). */
export const centerCount = (roles: RoleCounts): 3 | 4 => ((roles.alphaWolf ?? 0) > 0 ? 4 : 3)

/** Cards that must be selected for a given number of seats. */
export const requiredCards = (seats: number, roles: RoleCounts): number => seats + centerCount(roles)

export const selectedCount = (roles: RoleCounts): number =>
  Object.values(roles).reduce<number>((a, b) => a + (b ?? 0), 0)

/** Marks are in play (every player starts with a Mark of Clarity) when the deck holds a role that uses them. */
export const usesMarks = (roles: RoleCounts): boolean =>
  (Object.keys(roles) as RoleId[]).some((r) => (roles[r] ?? 0) > 0 && ROLES[r].tags.includes('marks'))

export type SetupError =
  | { kind: 'tooFewPlayers'; min: number }
  | { kind: 'tooManyPlayers'; max: number }
  | { kind: 'wrongCardCount'; required: number; selected: number }
  | { kind: 'belowMin'; role: RoleId; min: number }
  | { kind: 'aboveMax'; role: RoleId; max: number }
  | { kind: 'missingRequirement'; role: RoleId; requires: RoleId; min: number }

/** Validates a role selection for a table of `seats` players. Empty array = valid. */
export const validateSetup = (seats: number, roles: RoleCounts): SetupError[] => {
  const errors: SetupError[] = []
  if (seats < MIN_PLAYERS) errors.push({ kind: 'tooFewPlayers', min: MIN_PLAYERS })
  if (seats > MAX_PLAYERS) errors.push({ kind: 'tooManyPlayers', max: MAX_PLAYERS })
  const required = requiredCards(seats, roles)
  const selected = selectedCount(roles)
  if (required !== selected) errors.push({ kind: 'wrongCardCount', required, selected })
  for (const [id, count] of Object.entries(roles) as [RoleId, number | undefined][]) {
    if (!count) continue
    const def = ROLES[id]
    if (def.min !== undefined && count < def.min) errors.push({ kind: 'belowMin', role: id, min: def.min })
    const max = def.max ?? 1
    if (count > max) errors.push({ kind: 'aboveMax', role: id, max })
    for (const req of def.requires ?? []) {
      if ((roles[req.role] ?? 0) < req.min) {
        errors.push({ kind: 'missingRequirement', role: id, requires: req.role, min: req.min })
      }
    }
  }
  return errors
}

/** Expands a role-count map into a flat list of role ids (deterministic order: registry order). */
export const expandRoles = (roles: RoleCounts): RoleId[] =>
  (Object.keys(ROLES) as RoleId[]).flatMap((id) => Array<RoleId>(roles[id] ?? 0).fill(id))
